import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  CircularProgress,
  IconButton,
  Chip,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Download,
  Refresh,
  PeopleAlt,
  DirectionsCar,
  AttachMoney,
  CalendarToday
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart
} from 'recharts';
import axios from 'axios';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

const ReportsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState('30');
  const [alert, setAlert] = useState<{ show: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    show: false,
    message: '',
    severity: 'success'
  });

  // User Analytics Data
  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    newUsers: 0,
    growthRate: 0,
    passengerDriverRatio: '0:0'
  });
  const [userGrowthData, setUserGrowthData] = useState<any[]>([]);
  const [userTypeData, setUserTypeData] = useState<any[]>([]);

  // Ride Analytics Data
  const [rideStats, setRideStats] = useState({
    totalRides: 0,
    completionRate: 0,
    cancellationRate: 0,
    avgRidesPerDriver: 0,
    seatUtilization: 0
  });
  const [ridesByDay, setRidesByDay] = useState<any[]>([]);
  const [topRoutes, setTopRoutes] = useState<any[]>([]);
  const [demandByHour, setDemandByHour] = useState<any[]>([]);

  // Financial Analytics Data
  const [financialStats, setFinancialStats] = useState({
    totalRevenue: 0,
    avgTransactionValue: 0,
    commissionEarned: 0,
    revenueGrowth: 0
  });
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [paymentMethodData, setPaymentMethodData] = useState<any[]>([]);

  const apiUrl = process.env.NODE_ENV === 'production'
    ? 'https://rideclubnet.com/api'
    : 'http://localhost:3001/api';

  useEffect(() => {
    fetchReportData();
  }, [tabValue, dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };

      if (tabValue === 0) {
        // User Analytics
        const response = await axios.get(`${apiUrl}/admin/reports/users?days=${dateRange}`, { headers });
        const data = response.data;

        setUserStats({
          totalUsers: data.totalUsers || 0,
          activeUsers: data.activeUsers || 0,
          newUsers: data.newUsers || 0,
          growthRate: data.growthRate || 0,
          passengerDriverRatio: data.passengerDriverRatio || '0:0'
        });

        setUserGrowthData(data.growthData || []);
        setUserTypeData([
          { name: 'Passengers', value: data.totalPassengers || 0 },
          { name: 'Drivers', value: data.totalDrivers || 0 },
          { name: 'Both', value: data.both || 0 }
        ]);
      } else if (tabValue === 1) {
        // Ride Analytics
        const response = await axios.get(`${apiUrl}/admin/reports/rides?days=${dateRange}`, { headers });
        const data = response.data;

        setRideStats({
          totalRides: data.totalRides || 0,
          completionRate: data.completionRate || 0,
          cancellationRate: data.cancellationRate || 0,
          avgRidesPerDriver: data.avgRidesPerDriver || 0,
          seatUtilization: data.seatUtilization || 0
        });

        setRidesByDay(data.ridesByDay || []);
        setTopRoutes(data.topRoutes || []);
        setDemandByHour(data.demandByHour || []);
      } else if (tabValue === 2) {
        // Financial Analytics
        const response = await axios.get(`${apiUrl}/admin/reports/financial?days=${dateRange}`, { headers });
        const data = response.data;

        setFinancialStats({
          totalRevenue: data.totalRevenue || 0,
          avgTransactionValue: data.avgTransactionValue || 0,
          commissionEarned: data.commissionEarned || 0,
          revenueGrowth: data.revenueGrowth || 0
        });

        setRevenueData(data.revenueData || []);
        setPaymentMethodData(data.paymentMethodData || []);
      }
    } catch (error: any) {
      console.error('Failed to fetch report data:', error);
      showAlert('error', error.response?.data?.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: 'pdf' | 'excel' | 'csv') => {
    try {
      const token = localStorage.getItem('adminToken');
      const reportType = ['users', 'rides', 'financial'][tabValue];

      const response = await axios.get(
        `${apiUrl}/admin/reports/export/${reportType}?format=${format}&days=${dateRange}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      showAlert('success', 'Report exported successfully');
    } catch (error: any) {
      console.error('Export failed:', error);
      showAlert('error', 'Failed to export report');
    }
  };

  const showAlert = (severity: 'success' | 'error' | 'info', message: string) => {
    setAlert({ show: true, message, severity });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Reports & Analytics
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={dateRange}
              label="Time Period"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="7">Last 7 Days</MenuItem>
              <MenuItem value="30">Last 30 Days</MenuItem>
              <MenuItem value="90">Last 90 Days</MenuItem>
              <MenuItem value="180">Last 6 Months</MenuItem>
              <MenuItem value="365">Last Year</MenuItem>
            </Select>
          </FormControl>
          <IconButton onClick={fetchReportData} color="primary">
            <Refresh />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => handleExport('excel')}
          >
            Export Excel
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => handleExport('csv')}
          >
            Export CSV
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab icon={<PeopleAlt />} label="User Analytics" iconPosition="start" />
          <Tab icon={<DirectionsCar />} label="Ride Analytics" iconPosition="start" />
          <Tab icon={<AttachMoney />} label="Financial Analytics" iconPosition="start" />
          <Tab icon={<CalendarToday />} label="Custom Reports" iconPosition="start" />
        </Tabs>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* User Analytics Tab */}
          <TabPanel value={tabValue} index={0}>
            {/* Stats Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Users
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {userStats.totalUsers.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Active Users
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {userStats.activeUsers.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    New Users
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {userStats.newUsers.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Growth Rate
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4" fontWeight="bold" color={userStats.growthRate >= 0 ? 'success.main' : 'error.main'}>
                      {userStats.growthRate > 0 ? '+' : ''}{userStats.growthRate.toFixed(1)}%
                    </Typography>
                    {userStats.growthRate >= 0 ? <TrendingUp color="success" /> : <TrendingDown color="error" />}
                  </Box>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Passenger:Driver
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {userStats.passengerDriverRatio}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Charts */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mb: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  User Growth Trend
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={userGrowthData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="users" stroke="#8884d8" fill="#8884d8" />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  User Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={userTypeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {userTypeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          </TabPanel>

          {/* Ride Analytics Tab */}
          <TabPanel value={tabValue} index={1}>
            {/* Stats Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Rides
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {rideStats.totalRides.toLocaleString()}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Completion Rate
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {rideStats.completionRate.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Cancellation Rate
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="error.main">
                    {rideStats.cancellationRate.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg Rides/Driver
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {rideStats.avgRidesPerDriver.toFixed(1)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Seat Utilization
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="primary">
                    {rideStats.seatUtilization.toFixed(1)}%
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Charts */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr' }, gap: 3, mb: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Rides Over Time
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={ridesByDay}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="rides" stroke="#8884d8" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Demand by Hour
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={demandByHour}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="hour" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="rides" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Top Routes
                </Typography>
                <Box sx={{ mt: 2 }}>
                  {topRoutes.slice(0, 10).map((route, index) => (
                    <Box key={index} sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="body2">
                          {route.from} → {route.to}
                        </Typography>
                        <Chip label={`${route.count} rides`} size="small" />
                      </Box>
                      <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                        <Box
                          sx={{
                            width: `${(route.count / topRoutes[0]?.count) * 100}%`,
                            bgcolor: 'primary.main',
                            height: '100%',
                            borderRadius: 1
                          }}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
          </TabPanel>

          {/* Financial Analytics Tab */}
          <TabPanel value={tabValue} index={2}>
            {/* Stats Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Revenue
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {formatCurrency(financialStats.totalRevenue)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Avg Transaction
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {formatCurrency(financialStats.avgTransactionValue)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Commission Earned
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {formatCurrency(financialStats.commissionEarned)}
                  </Typography>
                </CardContent>
              </Card>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Revenue Growth
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4" fontWeight="bold" color={financialStats.revenueGrowth >= 0 ? 'success.main' : 'error.main'}>
                      {financialStats.revenueGrowth > 0 ? '+' : ''}{financialStats.revenueGrowth.toFixed(1)}%
                    </Typography>
                    {financialStats.revenueGrowth >= 0 ? <TrendingUp color="success" /> : <TrendingDown color="error" />}
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Charts */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Revenue Trend
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="revenue" stroke="#00C49F" fill="#00C49F" />
                  </AreaChart>
                </ResponsiveContainer>
              </Paper>

              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Payment Methods
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={paymentMethodData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {paymentMethodData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </Paper>
            </Box>
          </TabPanel>

          {/* Custom Reports Tab */}
          <TabPanel value={tabValue} index={3}>
            <Paper sx={{ p: 4 }}>
              <Typography variant="h6" gutterBottom>
                Custom Report Builder
              </Typography>
              <Typography color="textSecondary" gutterBottom>
                Build and schedule custom reports with your preferred metrics and filters
              </Typography>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Report Type</InputLabel>
                  <Select label="Report Type" defaultValue="">
                    <MenuItem value="user">User Report</MenuItem>
                    <MenuItem value="ride">Ride Report</MenuItem>
                    <MenuItem value="financial">Financial Report</MenuItem>
                    <MenuItem value="driver">Driver Performance</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Date Range</InputLabel>
                  <Select label="Date Range" defaultValue="30">
                    <MenuItem value="7">Last 7 Days</MenuItem>
                    <MenuItem value="30">Last 30 Days</MenuItem>
                    <MenuItem value="90">Last 90 Days</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>
                </FormControl>

                <TextField label="From Date" type="date" InputLabelProps={{ shrink: true }} fullWidth />
                <TextField label="To Date" type="date" InputLabelProps={{ shrink: true }} fullWidth />

                <FormControl fullWidth>
                  <InputLabel>Export Format</InputLabel>
                  <Select label="Export Format" defaultValue="excel">
                    <MenuItem value="pdf">PDF</MenuItem>
                    <MenuItem value="excel">Excel</MenuItem>
                    <MenuItem value="csv">CSV</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel>Schedule</InputLabel>
                  <Select label="Schedule" defaultValue="once">
                    <MenuItem value="once">Generate Once</MenuItem>
                    <MenuItem value="daily">Daily</MenuItem>
                    <MenuItem value="weekly">Weekly</MenuItem>
                    <MenuItem value="monthly">Monthly</MenuItem>
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                <Button variant="contained" startIcon={<Download />}>
                  Generate Report
                </Button>
                <Button variant="outlined">
                  Save Template
                </Button>
              </Box>

              <Divider sx={{ my: 4 }} />

              <Typography variant="h6" gutterBottom>
                Saved Report Templates
              </Typography>
              <Box sx={{ mt: 2 }}>
                <Alert severity="info">
                  No saved report templates yet. Create and save a report template to see it here.
                </Alert>
              </Box>
            </Paper>
          </TabPanel>
        </>
      )}

      <Snackbar
        open={alert.show}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, show: false })}
      >
        <Alert onClose={() => setAlert({ ...alert, show: false })} severity={alert.severity}>
          {alert.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportsPage;
